import { WALLOPS_HR2, type GeographicOrigin } from './reference';
import type { AssetSpec } from '../types';
import { RFIMS_SITES, RFIMS_COORDINATE_SOURCE } from '../../rfims/registry';

export interface SiteProfile extends GeographicOrigin {
  assetId: string;
  title: string;
  location: string;
  kind: 'campus' | 'optical' | 'field' | 'aircraft' | 'ocean' | 'military' | 'facility';
  altitudeM: number;
  altitudeMode: 'terrain' | 'absolute';
  demonstration: boolean;
  description: string;
  sourceUrl: string;
  sourceTitle: string;
  mapUrl?: string;
  buildingId?: number;
  facilityId?: string;
  extentM?: number;
  antennaId?: string;
}

const campus = {
  kind: 'campus' as const,
  altitudeM: 0,
  altitudeMode: 'terrain' as const,
  mapUrl: '/data/sites/wallops-hr2.json',
  demonstration: false,
};
export const SITE_PROFILES: Record<string, SiteProfile> = {
  ...Object.fromEntries(RFIMS_SITES.map(site => [site.id, {
    assetId: site.id, title: `${site.rmsId} · ${site.name}`, location: site.location,
    lat: site.lat, lon: site.lon, headingDeg: 0, kind: 'facility' as const,
    altitudeM: 0, altitudeMode: 'terrain' as const, demonstration: false,
    description: 'Client-supplied location with mapped surroundings. Installed antenna geometry, dimensions and RF pattern are unverified. Cyan marker is a location symbol.',
    sourceTitle: RFIMS_COORDINATE_SOURCE,
    sourceUrl: `https://www.openstreetmap.org/#map=19/${site.lat}/${site.lon}`,
    mapUrl: `/data/sites/${site.id}-v2.json`, facilityId: site.id, extentM: 350,
    antennaId: site.id,
  }])),
  soldier: {
    assetId: 'soldier',
    title: 'Fort Sill · demonstration',
    location: 'Oklahoma, USA',
    lat: 34.671,
    lon: -98.3978,
    headingDeg: 0,
    kind: 'military',
    altitudeM: 0,
    altitudeMode: 'terrain',
    demonstration: true,
    mapUrl: '/data/sites/fort-sill.json',
    description:
      'Representative field unit at Fort Sill, Oklahoma. The soldier, parked launcher and communications vehicle are an illustrative arrangement at this chosen display coordinate, not a live or surveyed equipment location. Surrounding roads and buildings use public map data.',
    sourceTitle: 'US Army Fort Sill reference',
    sourceUrl: 'https://www.army.mil/fcoe',
  },
  weather_center: {
    assetId: 'weather_center',
    title: 'National Weather Center',
    location: 'Norman, Oklahoma',
    lat: 35.181525854,
    lon: -97.439994645,
    headingDeg: 0,
    kind: 'facility',
    altitudeM: 0,
    altitudeMode: 'terrain',
    demonstration: false,
    mapUrl: '/data/sites/norman-weather.json',
    buildingId: 162153701,
    description:
      'Anchored at the mapped National Weather Center building in Norman, home to the NWS Norman forecast office. The existing analysis room is a representative cutaway, not a surveyed room, floor or reconstruction of the actual operations area. The host building is omitted at inspection scale to keep the analysts and displays visible.',
    sourceTitle: 'NOAA / NWS Norman office',
    sourceUrl: 'https://www.weather.gov/oun/office',
  },
  rf_antenna: {
    ...campus,
    ...WALLOPS_HR2,
    assetId: 'rf_antenna',
    title: 'WAL-HR2',
    location: 'Wallops, Virginia',
    description:
      'The WAL-HR2 Loader coordinate. HR2 is a 16.4 m antenna; this existing RF model preserves its original proportions and equipment, rather than representing a surveyed HR2 replica.',
    sourceTitle: 'NOAA antenna reference',
    sourceUrl:
      'https://noaasis.noaa.gov/pdf/docs/TWG2024/1005%20Wallops%20Command%20and%20Data%20Acquisition%20Update%20Sullivan.pdf',
  },
  ground_station: {
    ...campus,
    assetId: 'ground_station',
    title: 'Ground station',
    location: 'Wallops campus',
    lat: 37.946000544,
    lon: -75.458691876,
    headingDeg: 0,
    buildingId: 524425244,
    description:
      'Existing ground-station cutaway anchored at the large building selected in the Wallops aerial view. The position is the mapped footprint centroid; the internal layout and assigned function are representative, not a verified building survey.',
    sourceTitle: 'Mapped building footprint',
    sourceUrl: 'https://www.openstreetmap.org/way/524425244',
  },
  data_center: {
    ...campus,
    assetId: 'data_center',
    title: 'Data centre',
    location: 'Wallops campus',
    lat: 37.945926423,
    lon: -75.459069391,
    headingDeg: 0,
    buildingId: 524425246,
    description:
      'Existing data hall anchored at the smaller rectangular building beside the main facility. This is a presentation assignment from the selected aerial view, not confirmation that this building houses a data centre. The model retains its inspection scale.',
    sourceTitle: 'Mapped building footprint',
    sourceUrl: 'https://www.openstreetmap.org/way/524425246',
  },
  optical_antenna: {
    assetId: 'optical_antenna',
    title: 'Greenbelt optical station',
    location: 'GGAO, Maryland',
    lat: 39.0206,
    lon: -76.8275381253904,
    headingDeg: 0,
    kind: 'optical',
    altitudeM: 0,
    altitudeMode: 'terrain',
    demonstration: false,
    mapUrl: '/data/sites/greenbelt-optical.json',
    description:
      'NASA/ILRS publishes the approximate Greenbelt GODL (7105) reference as 39.020600 N, 76.827700 W. This representative telescope is displayed 14 m east of that reference to clear the mapped building. GGAO is an optical satellite laser-ranging station about 168 km northwest of Wallops; this is not a claim of an operational communications downlink here.',
    sourceTitle: 'NASA / ILRS station coordinates',
    sourceUrl: 'https://ilrs.gsfc.nasa.gov/network/stations/active/GODL_general.html',
  },
  er_2: {
    assetId: 'er_2',
    title: 'ER-2 · demonstration',
    location: 'California, USA',
    lat: 35.5,
    lon: -118,
    headingDeg: 90,
    kind: 'aircraft',
    altitudeM: 21336,
    altitudeMode: 'absolute',
    demonstration: true,
    description:
      'Fictional flight position over California at a nominal 70,000 ft (21.336 km), a NASA-documented ER-2 operating altitude. The aircraft is stationary for inspection. This is not ADS-B or live aircraft tracking; the globe uses a WGS84 ellipsoid-height approximation for the nominal altitude.',
    sourceTitle: 'NASA ER-2 operating altitude',
    sourceUrl: 'https://esdpubs.nasa.gov/aircraft/ER-2_-_AFRC',
  },
  terrestrial: {
    assetId: 'terrestrial',
    title: 'Field sensor · demonstration',
    location: 'Farmland near Ames, Iowa',
    lat: 42.016,
    lon: -93.747,
    headingDeg: 0,
    kind: 'field',
    altitudeM: 0,
    altitudeMode: 'terrain',
    demonstration: true,
    description:
      'Fictional micrometeorological station in Iowa farmland. Terrain and aerial surroundings are geographic; the sensor placement and close crop rows are illustrative. This does not identify an installed station or claim current crop conditions.',
    sourceTitle: 'View location',
    sourceUrl: 'https://www.openstreetmap.org/#map=17/42.016/-93.747',
  },
  boat: {
    assetId: 'boat',
    title: 'Oceanus · demonstration',
    location: 'Pacific Ocean off Newport, Oregon',
    lat: 44.6,
    lon: -125,
    headingDeg: 0,
    kind: 'ocean',
    altitudeM: 0,
    altitudeMode: 'absolute',
    demonstration: true,
    description:
      'Fictional offshore position, not live AIS. Oregon State retired R/V Oceanus on 21 November 2021; no current timestamped public position was verified. The representative vessel is placed at its visual waterline on the globe sea surface.',
    sourceTitle: 'Oregon State Oceanus record',
    sourceUrl: 'https://ceoas.oregonstate.edu/rv-oceanus',
  },
};

const generated = new WeakMap<AssetSpec, SiteProfile>();

/** Asset coordinates are the default; authored presentation profiles remain overrides.
 * Cache by spec identity so React store selectors return stable snapshots. */
export function siteProfile(asset?: AssetSpec | string | null): SiteProfile | null {
  if (!asset) return null;
  const id = typeof asset === 'string' ? asset : asset.id;
  if (SITE_PROFILES[id] && (typeof asset === 'string' || !RFIMS_SITES.some(s => s.id === id))) return SITE_PROFILES[id];
  if (typeof asset === 'string' || !asset.site || asset.space) return null;
  const cached = generated.get(asset);
  if (cached) return cached;
  const site = asset.site;
  const profile: SiteProfile = {
    assetId: id, title: asset.label, location: site.location ?? 'Catalogue location',
    lat: site.lat, lon: site.lon, headingDeg: site.headingDeg ?? 0,
    kind: 'facility', altitudeM: 0, altitudeMode: 'terrain', demonstration: false,
    description: site.description ?? asset.modelNote ?? 'Catalogue coordinate with representative equipment geometry; not a surveyed installation.',
    sourceTitle: site.sourceTitle ?? 'Catalogue map reference',
    sourceUrl: site.sourceUrl ?? `https://www.openstreetmap.org/#map=18/${site.lat}/${site.lon}`,
    mapUrl: site.mapUrl, facilityId: site.facilityId, extentM: site.extentM,
    antennaId: site.antennaId,
  };
  generated.set(asset, profile);
  return profile;
}
